import React, { useEffect, useState } from 'react'
import { fetchCmsItemBySlug } from '../lib/cms'
import InquiryModal from '../components/InquiryModal'

export default function PracticalInfoPage() {
  const [practical, setPractical] = useState(null)
  const [terms, setTerms] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    document.title = 'Praktisk Information & Betingelser - SKI & CO TRAVEL ApS'
    async function loadData() {
      const [pracData, termsData] = await Promise.all([
        fetchCmsItemBySlug('sider', 'praktisk-information'),
        fetchCmsItemBySlug('sider', 'betingelser'),
      ])
      setPractical(pracData)
      setTerms(termsData)
    }
    loadData()
  }, [])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-12">
      <div className="space-y-3 max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
          SKI & CO TRAVEL ApS
        </span>
        <h1 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
          Praktisk Information & Betingelser
        </h1>
        <p className="text-slate-300 text-base leading-relaxed">
          Oplysninger om liftkort, transport, forplejning og udstyr varierer fra rejse til rejse. Her kan du læse om de generelle rammer samt se betingelserne for vores skirejser.
        </p>
      </div>

      {/* Important Banner: Differs from trip to trip */}
      <div className="bg-gradient-to-r from-cyan-950/80 via-slate-900 to-slate-900 border border-cyan-500/30 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xl">
        <div className="space-y-2 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold uppercase tracking-wider">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>Vigtigt at vide</span>
          </div>
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-white">
            Praktisk information varierer fra rejse til rejse
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed">
            Når du booker en skirejse hos os, afhænger detaljer om transportform (fly/transfer, bus eller kør-selv), liftkortets dækning, måltider og bagageregler af den specifikke tur og destination.
          </p>
        </div>
        <a
          href="/skirejser"
          className="shrink-0 px-6 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-colors flex items-center gap-2 shadow-lg shadow-cyan-500/20"
        >
          <span>Se alle skirejser</span>
          <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </a>
      </div>

      {/* Categories breakdown */}
      <div className="space-y-6">
        <h2 className="font-heading font-bold text-2xl text-white">
          Hvad varierer pr. tur?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-xl">
              <i className="fa-solid fa-ticket" aria-hidden="true" />
            </div>
            <h3 className="font-heading font-bold text-lg text-white">Liftkort</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              Inklusion og dækningsområde (fx Dolomiti SuperSki eller Les 3 Vallées) er oplyst på den enkelte turside.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-xl">
              <i className="fa-solid fa-plane-arrival" aria-hidden="true" />
            </div>
            <h3 className="font-heading font-bold text-lg text-white">Transport & Bagage</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              Nogle rejser er med direkte fly og transfer, mens andre er tilrettelagt som kør-selv eller luksusbus.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-xl">
              <i className="fa-solid fa-utensils" aria-hidden="true" />
            </div>
            <h3 className="font-heading font-bold text-lg text-white">Forplejning</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              Forplejningsgraden (gourmet halvpension, morgenmad eller chalet med kok) fremgår af hotellets specifikation.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-xl">
              <i className="fa-solid fa-person-skiing" aria-hidden="true" />
            </div>
            <h3 className="font-heading font-bold text-lg text-white">Skileje & Skiskole</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              Kan tilvælges efter behov for de fleste ture. Vi samarbejder med lokale kvalitetspartnere.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Practical info card from CMS */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 space-y-4 shadow-xl">
          <h2 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
            <i className="fa-solid fa-circle-info text-cyan-400" aria-hidden="true" />
            <span>Praktisk Information</span>
          </h2>
          {practical?.broedtekst ? (
            <div
              className="prose prose-invert text-slate-300 text-sm leading-relaxed space-y-3"
              dangerouslySetInnerHTML={{ __html: practical.broedtekst }}
            />
          ) : (
            <p className="text-slate-300 text-sm leading-relaxed">
              Praktisk information varierer fra rejse til rejse. Se venligst de specifikke rejsesider for detaljer om liftkort, transport og indkvartering.
            </p>
          )}
        </div>

        {/* Terms card */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 space-y-4 shadow-xl">
          <h2 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
            <i className="fa-solid fa-file-contract text-cyan-400" aria-hidden="true" />
            <span>Betingelser og Forbehold</span>
          </h2>
          {terms?.broedtekst ? (
            <div
              className="prose prose-invert text-slate-300 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: terms.broedtekst }}
            />
          ) : (
            <p className="text-slate-300 text-sm leading-relaxed">
              Alle rejser og forespørgsler arrangeres af SKI & CO TRAVEL ApS, Kærmindevej 31, 6000 Kolding (Tlf: +45 51 58 94 52, Mail: claus@skiogco.dk).
            </p>
          )}
        </div>
      </div>

      {/* Direct Contact Notice */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 sm:p-8 text-center space-y-4">
        <h3 className="font-heading font-bold text-xl text-white">
          Har du spørgsmål til dit tilbud eller din rejse?
        </h3>
        <p className="text-slate-300 text-sm max-w-md mx-auto">
          Du er altid velkommen til at kontakte os direkte for personlig rådgivning.
        </p>
        <div className="flex justify-center gap-4 pt-2">
          <button
            onClick={() => setModalOpen(true)}
            className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-colors"
          >
            Send en forespørgsel
          </button>
        </div>
      </div>

      <InquiryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}
